/**
 * A floating panel anchored to a control, portaled out of its card.
 *
 * ── The bug this exists to fix ──────────────────────────────────────────────
 * Jeff, 2026-09-10, on Start an agreement: "this search box… gets cut off when
 * looking for the customer." The customer combobox's results panel was
 * `position: absolute` inside a `.section`, and `.crm-scope .section` sets
 * `overflow: hidden`. So the card clipped its own dropdown at the section
 * border — about one row of results was visible and the rest was simply gone.
 *
 * That `overflow: hidden` cannot be removed. It is the only thing keeping a
 * full-bleed table's zebra row from painting over the card's rounded corner,
 * and `full-bleed-corner-guard.spec.ts` pins the dependency precisely because
 * "a card needs a dropdown to escape its bounds" is the plausible reason
 * someone would delete it. That spec called this exact situation, so the fix
 * goes the other way: the panel leaves the card instead of the card opening up.
 *
 * ── Why a portal rather than a bigger z-index ───────────────────────────────
 * `z-index` does nothing against `overflow: hidden` — clipping is not painting
 * order. The panel has to leave the clipping ancestor's subtree entirely, and
 * `createPortal` to `document.body` is the only way to do that.
 *
 * ── Positioning is `fixed`, and recomputed on scroll ────────────────────────
 * Out of the flow, the panel has to be told where its anchor is. `fixed` makes
 * that a viewport-relative measurement, which means it goes stale the moment
 * anything scrolls — so scroll is listened for in the CAPTURE phase, which is
 * the only way to hear a scroll on an ancestor element rather than the page.
 * The alternative (`absolute` + page coordinates) survives page scroll and
 * breaks on ancestor scroll, and this app has both.
 */
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/** The measurement the panel needs. A subset of DOMRect, so it is testable. */
export interface AnchorRect {
  top: number;
  left: number;
  bottom: number;
  width: number;
}

export interface Placement {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  /** Which way it opened. Exposed so a caller can flip a chevron. */
  direction: 'down' | 'up';
}

/**
 * Where the panel goes, given its anchor and the viewport.
 *
 * PURE, and unit-tested, because jsdom has no layout: nothing in a render test
 * can prove a panel is on screen. What can be proven is the arithmetic that
 * decides, which is the same split `tableScrollMaxHeight` uses.
 *
 * Opens downward whenever downward is workable, because that is where a reader
 * looks after typing. It flips up only when below is genuinely too cramped AND
 * above is roomier — a panel that flips for a 4px difference is worse than one
 * that never flips, since the results appear somewhere different each time.
 */
export function panelPlacement(
  rect: AnchorRect,
  viewportHeight: number,
  gap = 4,
  desired = 280,
  minimum = 140,
): Placement {
  const below = viewportHeight - rect.bottom - gap;
  const above = rect.top - gap;

  if (below >= minimum || below >= above) {
    return {
      top: rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(0, Math.min(desired, below)),
      direction: 'down',
    };
  }

  const maxHeight = Math.max(0, Math.min(desired, above));
  return {
    top: rect.top - gap - maxHeight,
    left: rect.left,
    width: rect.width,
    maxHeight,
    direction: 'up',
  };
}

/**
 * The marker that lets an outside-click handler recognise a portaled panel.
 *
 * PORTALING BREAKS `ref.contains(target)`, and it breaks it in the most
 * confusing possible way: the panel appears correctly, and then clicking an
 * option does nothing. The mousedown lands on a node that is no longer a
 * descendant of the anchor, the handler reads that as a click outside, and the
 * panel closes before the click can resolve to a selection.
 *
 * So every panel carries its own testId as a data attribute, and `isInDropdown`
 * is the check an outside-click handler must use INSTEAD of trusting the DOM
 * tree it can see.
 */
export const DROPDOWN_OWNER_ATTR = 'data-dropdown-owner';

export function isInDropdown(
  target: EventTarget | null,
  testId: string,
): boolean {
  const el = target as Element | null;
  if (!el || typeof el.closest !== 'function') return false;
  return el.closest(`[${DROPDOWN_OWNER_ATTR}="${cssEscape(testId)}"]`) !== null;
}

/** testIds are author-controlled kebab-case, but an attribute selector still
 *  has to be well-formed — quote-escaping is the whole of what is needed. */
function cssEscape(v: string): string {
  return v.replace(/["\\]/g, '\\$&');
}

export function DropdownPanel({
  anchor,
  children,
  className = 'detail-panel',
  id,
  role,
  testId,
}: {
  /**
   * The element the panel hangs off — usually the input's wrapper.
   *
   * `someRef.current` is fine when the anchor is mounted well before the panel
   * can open, which is the combobox case. If the two can first render on the
   * same pass, pass a callback ref held in state instead: `.current` read
   * during render is null on that pass and nothing re-renders when it attaches.
   */
  anchor: HTMLElement | null;
  children: React.ReactNode;
  className?: string;
  id?: string;
  role?: string;
  testId: string;
}) {
  const [place, setPlace] = useState<Placement | null>(null);

  const measure = useCallback(() => {
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    setPlace(
      panelPlacement(
        { top: r.top, left: r.left, bottom: r.bottom, width: r.width },
        window.innerHeight,
      ),
    );
  }, [anchor]);

  // BEFORE PAINT. A useEffect here renders the panel at 0,0 for one frame,
  // which is a visible flash in the top-left corner of the window.
  useLayoutEffect(measure, [measure]);

  useEffect(() => {
    if (!anchor) return;
    // `capture: true` is the load-bearing half — a scroll event does not
    // bubble, so a listener on window only ever hears the page scroll and
    // never the `.table-scroll` or drawer the anchor may sit inside.
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [anchor, measure]);

  if (!anchor || !place) return null;

  return createPortal(
    /*
     * `crm-scope` on the portal's own wrapper. `document.body` is outside the
     * app's, and every token this panel reads (`--text-muted`, `--bg-card`) is
     * DEFINED on that class — portaled without it the panel renders unstyled
     * with every variable resolving to nothing. Same reason Modal.tsx does it.
     */
    <div className="crm-scope">
      <div
        className={className}
        data-direction={place.direction}
        data-testid={testId}
        {...{ [DROPDOWN_OWNER_ATTR]: testId }}
        id={id}
        role={role}
        style={{
          position: 'fixed',
          top: place.top,
          left: place.left,
          width: place.width,
          maxHeight: place.maxHeight,
          overflowY: 'auto',
          // Above the dialog layer's backdrop is NOT wanted — a dropdown
          // inside a modal is still inside it — but above every card and
          // sticky header in the page, which top out in the twenties.
          zIndex: 60,
          // --text-muted, not a --border* token: every border token in this
          // scope resolves too faint to bound a floating panel. See
          // crm/disclosure-affordance.spec.ts.
          border: '1px solid var(--text-muted)',
          /*
           * `--bg-card` (white), NOT `--bg-dark`.
           *
           * The panel this replaced used `--bg-dark` — hsl(221 23% 11%), very
           * nearly black — while its option rows are `.btn.btn-sm`, whose text
           * colour is the dark ink meant for a light surface. Measured in the
           * running app: background rgb(22,26,35) against text rgb(31,37,50),
           * a contrast ratio of about 1.05:1. The customer names were not dim,
           * they were invisible; the only legible part of each row was the
           * muted grey account number underneath.
           *
           * That is the other half of what Jeff reported as the box being "cut
           * off" — the visible strip of panel had nothing readable in it. So
           * the fix is not to lighten the text but to put the panel on the
           * surface its contents were designed for, which is also what
           * CustomerPicker's panel already does.
           */
          background: 'var(--bg-card)',
          borderRadius: 'var(--radius)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
